import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import common from './locales/en/common.json';
import forBusinesses from './locales/en/forBusinesses.json';
import auth from './locales/en/auth.json';
import legal from './locales/en/legal.json';
import search from './locales/en/search.json';
import planner from './locales/en/planner.json';
import feedCard from './locales/en/feedCard.json';
import home from './locales/en/home.json';
import tripPlanner from './locales/en/tripPlanner.json';
import details from './locales/en/details.json';
import labels from './locales/en/labels.json';
import savedItems from './locales/en/savedItems.json';
import profile from './locales/en/profile.json';
import memories from './locales/en/memories.json';
import modals from './locales/en/modals.json';
import social from './locales/en/social.json';
import reviews from './locales/en/reviews.json';
import settings from './locales/en/settings.json';
import albums from './locales/en/albums.json';
import cards from './locales/en/cards.json';
import maps from './locales/en/maps.json';
import place from './locales/en/place.json';
import onboarding from './locales/en/onboarding.json';
import destination from './locales/en/destination.json';
import takedown from './locales/en/takedown.json';

// Dutch (nl) — Belgian Standard Dutch, GTM beachhead is Flanders. Only the
// namespaces that have been translated + validated are registered; every
// other key falls back to English via `fallbackLng`. Add a namespace here as
// each `nl/<ns>.json` passes `scripts/validate_locale.js`.
import nlCommon from './locales/nl/common.json';

import { resolveInitialLanguage } from './utils/languagePreference';

/**
 * i18n bootstrap — English is the source of truth; Dutch is being layered in
 * namespace by namespace. Catalogs are bundled statically (fine at two
 * locales; switch to per-namespace dynamic import at 3+ — see docs/I18N.md).
 * Convention: docs/I18N.md.
 *
 * Imported once from src/index.js (app) and src/setupTests.js (Jest — tests
 * never execute index.js, so they need their own import).
 *
 * `lng` is resolved from the device's stored choice / browser at load time;
 * the account-level preference is applied afterwards by AuthContext once auth
 * resolves. Any nl gap renders English via `fallbackLng`, never a raw key.
 */
i18n.use(initReactI18next).init({
  lng: resolveInitialLanguage(),
  fallbackLng: 'en',
  supportedLngs: ['en', 'nl'],
  load: 'languageOnly', // 'nl-BE' → 'nl'
  nonExplicitSupportedLngs: true,
  resources: {
    en: { common, forBusinesses, auth, legal, search, planner, feedCard, home, tripPlanner, details, labels, savedItems, profile, memories, modals, social, reviews, settings, albums, cards, maps, place, onboarding, destination, takedown },
    nl: { common: nlCommon }
  },
  defaultNS: 'common',
  interpolation: { escapeValue: false }, // React already escapes rendered values
  returnEmptyString: false,
  debug: false,
  // Dev-only tripwire: a key that falls back to English (or renders as a raw
  // key path) during the nl translation pass logs once per key. No-op in
  // production and for the 'en' base language.
  saveMissing: process.env.NODE_ENV === 'development',
  missingKeyHandler: (lngs, ns, key) => {
    if (process.env.NODE_ENV === 'development' && !(lngs.length === 1 && lngs[0] === 'en')) {
      console.warn(`[i18n] missing key for ${lngs.join(',')}: ${ns}:${key}`);
    }
  }
});

// Keep <html lang> in sync with the active language — screen readers pick
// pronunciation rules from it, and search engines read it. Covers both the
// initial (detected) language and every later switch.
const syncHtmlLang = (lng) => {
  if (typeof document !== 'undefined' && lng) {
    document.documentElement.lang = lng;
  }
};
syncHtmlLang(i18n.language);
i18n.on('languageChanged', syncHtmlLang);

export default i18n;
